/**
 * API client with auth handling. Includes credentials for cookies.
 * v2: Auto-fetches and includes CSRF tokens on state-changing requests.
 *
 * The backend CSRF middleware (csrfProtection) exempts:
 *  - Login, Register, Google OAuth, Forgot Password, Reset Password endpoints
 *  - Public endpoints (/api/public/*, /api/verify/*, /api/files/*, /api/contact)
 *  - FormData uploads (multipart/form-data)
 *
 * For all other POST/PATCH/PUT/DELETE requests, the client must include
 * an X-CSRF-Token header. This client auto-fetches a token from
 * /api/auth/csrf-token when needed (and caches it in memory).
 */
import { API_URL } from "./constants"

export class ApiError extends Error {
  status: number
  code?: string
  constructor(message: string, status: number, code?: string) {
    super(message)
    this.status = status
    this.code = code
  }
}

// CSRF token cache (in-memory, refreshed when expired)
let cachedCsrfToken: string | null = null
let csrfTokenExpiry = 0
let csrfFetchPromise: Promise<string> | null = null

async function getCsrfToken(): Promise<string> {
  // Return cached token if still valid (5-min buffer)
  if (cachedCsrfToken && Date.now() < csrfTokenExpiry - 5 * 60 * 1000) {
    return cachedCsrfToken
  }
  // Dedupe concurrent fetches
  if (csrfFetchPromise) return csrfFetchPromise
  csrfFetchPromise = fetch(`${API_URL}/api/auth/csrf-token`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  })
    .then(res => {
      if (!res.ok) throw new Error("Failed to fetch CSRF token")
      return res.json()
    })
    .then((data: { token: string; expiresIn: number }) => {
      cachedCsrfToken = data.token
      csrfTokenExpiry = Date.now() + data.expiresIn
      return data.token
    })
    .finally(() => {
      csrfFetchPromise = null
    })
  return csrfFetchPromise
}

// Paths exempt from CSRF (mirrors backend EXEMPT_PATHS)
const CSRF_EXEMPT_PREFIXES = [
  "/api/auth/login",
  "/api/auth/register",
  "/api/auth/google",
  "/api/auth/forgot-password",
  "/api/auth/reset-password",
  "/api/auth/csrf-token",
  "/api/public/",
  "/api/verify/",
  "/api/files/",
  "/api/contact",
  "/api/ai/",  // AI chat is public (rate-limited instead)
  "/api/setup",
]

function isCsrfExempt(path: string): boolean {
  return CSRF_EXEMPT_PREFIXES.some(p => path === p || path.startsWith(p))
}

async function buildHeaders(body: any, path: string): Promise<Record<string, string>> {
  const headers: Record<string, string> = { Accept: "application/json" }
  if (body instanceof FormData) {
    // FormData — let browser set Content-Type with boundary; CSRF exempt for multipart
    return headers
  }
  headers["Content-Type"] = "application/json"
  // Add CSRF token if not exempt
  if (!isCsrfExempt(path)) {
    try {
      const token = await getCsrfToken()
      headers["X-CSRF-Token"] = token
    } catch (e) {
      console.warn("Failed to get CSRF token:", e)
    }
  }
  return headers
}

async function handleResponse(res: Response) {
  if (!res.ok) {
    let errorMsg = `Request failed (${res.status})`
    let code: string | undefined
    try {
      const body = await res.json()
      errorMsg = body.error || body.message || errorMsg
      code = body.code
    } catch {}
    // If CSRF token is invalid/expired, clear cache so next request fetches a fresh one
    if (code === "CSRF_INVALID" || code === "CSRF_MISSING") {
      cachedCsrfToken = null
    }
    throw new ApiError(errorMsg, res.status, code)
  }
  // Handle CSV / text responses
  const contentType = res.headers.get("content-type") || ""
  if (contentType.includes("application/json")) {
    return res.json()
  }
  if (contentType.includes("text/")) {
    return res.text()
  }
  if (contentType.includes("blob") || contentType.includes("octet-stream")) {
    return res.blob()
  }
  return res.json()
}

async function fetchWithRetry(
  url: string,
  options: RequestInit,
  path: string,
  body: any,
  isRetry = false,
): Promise<any> {
  const res = await fetch(url, options)
  // If CSRF token expired, clear cache and retry once with a fresh token
  if (res.status === 403 && !isRetry && !isCsrfExempt(path)) {
    try {
      const errBody = await res.clone().json()
      if (errBody.code === "CSRF_INVALID" || errBody.code === "CSRF_MISSING") {
        cachedCsrfToken = null
        const newToken = await getCsrfToken()
        const newHeaders = new Headers(options.headers)
        newHeaders.set("X-CSRF-Token", newToken)
        return fetchWithRetry(url, { ...options, headers: newHeaders }, path, body, true)
      }
    } catch {}
  }
  return handleResponse(res)
}

export const api = {
  async get<T = any>(path: string, params?: Record<string, any>): Promise<T> {
    const url = new URL(`${API_URL}${path}`, window.location.origin)
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== "") {
          url.searchParams.set(k, String(v))
        }
      }
    }
    const res = await fetch(url.toString(), {
      credentials: "include",
      headers: { Accept: "application/json" },
    })
    return handleResponse(res)
  },

  async post<T = any>(path: string, body?: any): Promise<T> {
    const headers = await buildHeaders(body, path)
    const bodyStr = body instanceof FormData ? body : body ? JSON.stringify(body) : undefined
    return fetchWithRetry(
      `${API_URL}${path}`,
      { method: "POST", credentials: "include", headers, body: bodyStr },
      path,
      body,
    )
  },

  async patch<T = any>(path: string, body?: any): Promise<T> {
    const headers = await buildHeaders(body, path)
    const bodyStr = body ? JSON.stringify(body) : undefined
    return fetchWithRetry(
      `${API_URL}${path}`,
      { method: "PATCH", credentials: "include", headers, body: bodyStr },
      path,
      body,
    )
  },

  async put<T = any>(path: string, body?: any): Promise<T> {
    const headers = await buildHeaders(body, path)
    const bodyStr = body ? JSON.stringify(body) : undefined
    return fetchWithRetry(
      `${API_URL}${path}`,
      { method: "PUT", credentials: "include", headers, body: bodyStr },
      path,
      body,
    )
  },

  async delete<T = any>(path: string): Promise<T> {
    const headers = await buildHeaders(null, path)
    return fetchWithRetry(
      `${API_URL}${path}`,
      { method: "DELETE", credentials: "include", headers },
      path,
      null,
    )
  },

  async downloadCsv(path: string, filename: string) {
    const headers: Record<string, string> = { Accept: "text/csv" }
    if (!isCsrfExempt(path)) {
      try {
        const token = await getCsrfToken()
        headers["X-CSRF-Token"] = token
      } catch {}
    }
    const res = await fetch(`${API_URL}${path}`, {
      credentials: "include",
      headers,
    })
    if (!res.ok) throw new ApiError("Download failed", res.status)
    const text = await res.text()
    const blob = new Blob([text], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  },
}
